/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useMutation, UseMutationResult } from '@tanstack/react-query';
import React, { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  dateToEpoch,
  epochToDate,
  formatDate,
  formatDateYYYYMMDD,
  hyphenateCompCode,
  isObjEmpty,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
} from '../../common/UtilityFunctions';
import * as constants from '../../common/constants';
import {
  CompCodeResponse,
  ImpoundPolicyListResponse,
  MiscPayeeResponseElement,
  Parcel,
  QIDListResponse,
  ServicingArrayEntry,
  submitImpoundInfo,
  SubmitImpoundResponse,
  TaxAssessorsResponse,
} from '../../common/queryFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';
import css from '../../css/loan-escrow-accruals-styles/ImpoundForm.module.css';
import DefaultModal from '../modals/DefaultModal';
import useQidListFullResponse from '../../common/hooks/useQidListFullResponse';
import useCompCodes from '../../common/hooks/useCompCodes';
import useMiscPayees from '../../common/hooks/useMiscPayees';
import useTaxAssessors from '../../common/hooks/useTaxAssessors';

interface ServicingArrayEntryInput {
  payAgent: boolean;
  unitNo: string;
  agentCompany: string;
  servArrComment: string;
  policyNo: string;
  servArrAmountPaid: number;
  servArrSupAmountPaid: number;
  policyStatus: boolean;
  policyStatusString: string; //a special field meant to preserve the string value of the policy status
  companyToPay: string;
  servArrPayeeNumber: string;
  servArrCancelDate: Date;
  servArrSupAmountDue: string;
  servArrAmountDue: string;
  servArrCoverage: string;
  $index: number;
}
interface ParcelInput {
  //this interface is identical to the Parcel interface but the amountDue is stored as a string for ease of form handling
  amountDue: string;
  parcelNo: string;
  amountPaid: number;
  assessorCode: string;
  supAmountDue: string;
  parcelStatus: boolean;
  parcelStatusString: string;
  supAmountPaid: number;
  parcelArrComment: string;
  $index: number;
}
interface RequestData {
  impoundGroup: string;
  impoundType: string;
  sendData: { [key: string]: any };
  renewalDte: string;
  imp1EntryExists: boolean;
}
interface CustomDropdownEventTarget {
  name: string;
  value: string;
  type: string;
  checked: boolean;
}
interface CustomDropdownEvent {
  target: CustomDropdownEventTarget;
}
interface FormInput {
  impoundExists: boolean;
  activeStatus: boolean;
  loan: string;
  impoundType: string;
  renewalDte: Date;
  servPremium: string;
  servSupPremium: string;
  servAmtPaid: number;
  servSupAmtPaid: number;
  insuranceComp: string;
  payeeNumber: string;
  policyNum: string;
  agentCompanyCode: string;
  coverageAmt: string;
  cancelationDte: Date;
  escrowed: boolean;
  parcels: Array<Parcel>;
  servicingArrayEntries: Array<ServicingArrayEntry>;
  comment: string;
}
interface ComponentProps {
  stateCode: string;
  miBusinessTypeCode: string;
  insuranceBusinessTypeCode: string;
  hoaBusinessTypeCode: string;
  taxImpounds: Array<ImpoundPolicyListResponse>;
  hazImpounds: Array<ImpoundPolicyListResponse>;
  miImpounds: Array<ImpoundPolicyListResponse>;
  hoaImpounds: Array<ImpoundPolicyListResponse>;
  grImpounds: Array<ImpoundPolicyListResponse>;
  miEscrowed: Record<string, boolean>;
  impTypes: Array<string>;
  isLoanFhaUsda: boolean;
  handleParentRefetch: (type: string) => Promise<void>;
}
const ImpoundForm: React.FC<ComponentProps> = (props) => {
  const { loanNumber } = useLoanContext();
  const { token } = useAuthContext();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [formInput, setFormInput] = useState<FormInput>({
    impoundExists: false,
    activeStatus: true,
    loan: loanNumber,
    impoundType: '',
    renewalDte: null,
    servPremium: '0.00',
    servSupPremium: '0.00',
    servAmtPaid: 0,
    servSupAmtPaid: 0,
    insuranceComp: '',
    payeeNumber: '',
    policyNum: '',
    agentCompanyCode: '',
    coverageAmt: '0.00',
    cancelationDte: null,
    escrowed: false,
    parcels: [],
    servicingArrayEntries: [],
    comment: '',
  });

  const [tempServicingArrayEntries, setTempServicingArrayEntries] = useState<Array<ServicingArrayEntryInput>>([]);
  const [servicingArrayEntryValues, setServicingArrayEntryValues] = useState<ServicingArrayEntryInput>({
    payAgent: false,
    unitNo: '1', //default unit to be 1 in the case of single payees
    agentCompany: '',
    servArrComment: '',
    policyNo: '',
    servArrAmountPaid: 0.0,
    servArrSupAmountPaid: 0.0,
    policyStatus: true,
    policyStatusString: constants.impPolicyStatusEscrowed, //default to escrowed
    companyToPay: '',
    servArrPayeeNumber: '',
    servArrCancelDate: null,
    servArrSupAmountDue: '0.00',
    servArrAmountDue: '0.00',
    servArrCoverage: '0.00',
    $index: 0,
  });

  const [tempParcels, setTempParcels] = useState<Array<ParcelInput>>([]);
  const [parcelValues, setParcelValues] = useState<ParcelInput>({
    amountDue: '0.00',
    parcelNo: '',
    amountPaid: 0,
    assessorCode: '',
    supAmountDue: '0.00',
    parcelStatus: true,
    parcelStatusString: constants.impPolicyStatusEscrowed,
    supAmountPaid: 0,
    parcelArrComment: '',
    $index: 0,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [parcelErrors, setParcelErrors] = useState<Record<string, string>>({});
  const [servicingArrayEntryErrors, setServicingArrayEntryErrors] = useState<Record<string, string>>({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const handleParentRefetch = props.handleParentRefetch;

  //need to create
  const submitMutation: UseMutationResult<SubmitImpoundResponse, Error, RequestData, unknown> = useMutation({
    mutationFn: (requestData: RequestData) => {
      const controller: AbortController = new AbortController();
      abortControllerRef.current = controller;
      const signal: AbortSignal = controller.signal;
      const mutationPromise: Promise<SubmitImpoundResponse> = submitImpoundInfo(
        signal,
        token,
        loanNumber,
        requestData.impoundGroup,
        requestData.impoundType,
        requestData.sendData,
        requestData.renewalDte,
        requestData.imp1EntryExists,
      );
      (mutationPromise as any).cancel = () => controller.abort();
      return mutationPromise;
    },
  });

  const insuranceBusinessTypeCode: Array<string> = [props.insuranceBusinessTypeCode];
  const miBusinessTypeCode: Array<string> = [props.miBusinessTypeCode];
  const hoaBusinessTypeCode: Array<string> = [props.hoaBusinessTypeCode];

  const {
    data: impTypeQids,
    status: impTypeStatus,
    error: impTypeError,
  } = useQidListFullResponse(token, constants.impTypeQid);

  const {
    data: insuranceCompanies,
    status: insCompStatus,
    error: insCompError,
  } = useCompCodes(insuranceBusinessTypeCode, token);

  const { data: miCompanies, status: miCompStatus, error: miCompError } = useCompCodes(miBusinessTypeCode, token);

  const { data: hoaCompanies, status: hoaCompStatus, error: hoaCompError } = useCompCodes(hoaBusinessTypeCode, token);

  const {
    data: miscPayees,
    status: miscPayeesStatus,
    error: miscPayeesError,
  } = useMiscPayees('impoundFormMiscPayees', token);

  const {
    data: taxAssessors,
    status: taxAssessStatus,
    error: taxAssessError,
  } = useTaxAssessors(formInput.impoundType, props.stateCode, token);

  const clearFormInput = useCallback(() => {
    setFormInput({
      impoundExists: false,
      activeStatus: true,
      loan: loanNumber,
      impoundType: '',
      renewalDte: null,
      servPremium: '0.00',
      servSupPremium: '0.00',
      servAmtPaid: 0,
      servSupAmtPaid: 0,
      insuranceComp: '',
      payeeNumber: '',
      policyNum: '',
      agentCompanyCode: '',
      coverageAmt: '0.00',
      cancelationDte: null,
      escrowed: false,
      parcels: [],
      servicingArrayEntries: [],
      comment: '',
    });
  }, [loanNumber]);

  const clearParcelValues = useCallback(() => {
    setParcelValues({
      amountDue: '0.00',
      parcelNo: '',
      amountPaid: 0,
      assessorCode: '',
      supAmountDue: '0.00',
      parcelStatus: true,
      parcelStatusString: constants.impPolicyStatusEscrowed,
      supAmountPaid: 0,
      parcelArrComment: '',
      $index: 0,
    });
  }, []);

  const clearServicingArrayEntryValues = useCallback(() => {
    setServicingArrayEntryValues({
      payAgent: false,
      unitNo: '1', //default unit to be 1 in the case of single payees
      agentCompany: '',
      servArrComment: '',
      policyNo: '',
      servArrAmountPaid: 0.0,
      servArrSupAmountPaid: 0.0,
      policyStatus: true,
      policyStatusString: constants.impPolicyStatusEscrowed, //default to escrowed
      companyToPay: '',
      servArrPayeeNumber: '',
      servArrCancelDate: null,
      servArrSupAmountDue: '0.00',
      servArrAmountDue: '0.00',
      servArrCoverage: '0.00',
      $index: 0,
    });
  }, []);

  const clearInputs = useCallback(() => {
    clearFormInput();
    clearParcelValues();
    clearServicingArrayEntryValues();
    setTempParcels([]);
    setTempServicingArrayEntries([]);
  }, [clearFormInput, clearParcelValues, clearServicingArrayEntryValues]);

  const resetMutations = useCallback(() => {
    //reset mutations' status
    submitMutation.reset();
  }, [submitMutation]);

  const refetch = useCallback(async () => {
    if (submitMutation.status === 'success' || submitMutation.status === 'error') {
      await handleParentRefetch(formInput.impoundType);
    }
  }, [formInput.impoundType, submitMutation.status, handleParentRefetch]);

  const reset = useCallback(() => {
    refetch();
    clearInputs();
    resetMutations();
  }, [clearInputs, resetMutations, refetch]);

  const StatusModal = () => {
    const mutationErrorsPresent = submitMutation.status === 'error';
    const mutationSuccessPresent = submitMutation.status === 'success';
    const mutationPending = submitMutation.status === 'pending';

    let titleStr = '';
    if (mutationErrorsPresent) titleStr = 'Error';
    else if (mutationSuccessPresent) titleStr = 'Success';
    else if (mutationPending) titleStr = 'Processing...';

    return (
      <DefaultModal
        open={mutationErrorsPresent || mutationSuccessPresent || mutationPending}
        title={<div className={css.modalTitle}>{titleStr}</div>}
        content={
          <div className={css.modalContent}>
            {submitMutation.status === 'pending' && <div>Processing impound data submission...</div>}
            {submitMutation.status === 'error' && (
              <div>Failed to submit impound data! {submitMutation.error.message}</div>
            )}
            {submitMutation.status === 'success' && <div> Impound data submitted successfully!</div>}
          </div>
        }
        footer={
          (mutationErrorsPresent || mutationSuccessPresent) && (
            <button className={css.modalButton} onClick={reset}>
              {'OK'}
            </button>
          )
        }
      ></DefaultModal>
    );
  };

  const setCurrentFormInput = useCallback(
    (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
      const value: string = event.target.value; //incoming value might be the imp type or the renewal date
      let impType: string = '';
      let dateString: string = '';
      if (event.target.name === 'impoundType' && value !== null) {
        impType = value;
        dateString = formatDateYYYYMMDD(new Date()); //when changing imp type, renewal date default to today
      } else if (event.target.name === 'renewalDte' && value !== null) {
        impType = formInput.impoundType; //since renewal date can only be changed when there is a present imp type simply use the current imp type
        dateString = value;
      }
      let dateValue: Date = null;
      let impound: ImpoundPolicyListResponse = null;
      dateString ? (dateString = `${dateString}T00:00:00-08:00`) : (dateString = ''); //convert to UTC-8 to ensure no time zone date shift occurs
      dateString ? (dateValue = new Date(dateString)) : (dateValue = null);
      //flatten the impound lists
      const flattenedTaxImpounds: Array<ImpoundPolicyListResponse> = props.taxImpounds.flatMap((impList) => impList);
      const flattenedHazImpounds: Array<ImpoundPolicyListResponse> = props.hazImpounds.flatMap((impList) => impList);
      const flattenedMiImpounds: Array<ImpoundPolicyListResponse> = props.miImpounds.flatMap((impList) => impList);
      const flattenedHoaImpounds: Array<ImpoundPolicyListResponse> = props.hoaImpounds.flatMap((impList) => impList);
      const flattenedGrImpounds: Array<ImpoundPolicyListResponse> = props.grImpounds.flatMap((impList) => impList);

      if (impType.startsWith('TX')) {
        impound = flattenedTaxImpounds.find(
          (impound) =>
            impound.impoundType === impType && formatDate(epochToDate(impound.renewalDte)) === formatDate(dateValue),
        );
      }
      if (impType.startsWith('IN')) {
        impound = flattenedHazImpounds.find(
          (impound) =>
            impound.impoundType === impType && formatDate(epochToDate(impound.renewalDte)) === formatDate(dateValue),
        );
      }
      if (impType.startsWith('MI')) {
        impound = flattenedMiImpounds.find(
          (impound) =>
            impound.impoundType === impType && formatDate(epochToDate(impound.renewalDte)) === formatDate(dateValue),
        );
      }
      if (impType.startsWith('HA')) {
        impound = flattenedHoaImpounds.find(
          (impound) =>
            impound.impoundType === impType && formatDate(epochToDate(impound.renewalDte)) === formatDate(dateValue),
        );
      }
      if (impType.startsWith('GR')) {
        impound = flattenedGrImpounds.find(
          (impound) =>
            impound.impoundType === impType && formatDate(epochToDate(impound.renewalDte)) === formatDate(dateValue),
        );
      }

      if (!impound) {
        console.log('impound does not exist');
        setFormInput({
          impoundExists: false,
          activeStatus: true,
          loan: loanNumber,
          impoundType: impType,
          renewalDte: dateValue,
          servPremium: '0.00',
          servSupPremium: '0.00',
          servAmtPaid: 0,
          servSupAmtPaid: 0,
          insuranceComp: '',
          payeeNumber: '',
          policyNum: '',
          agentCompanyCode: '',
          coverageAmt: '0.00',
          cancelationDte: null,
          escrowed: false,
          parcels: [],
          servicingArrayEntries: [],
          comment: '',
        });
        clearParcelValues();
        clearServicingArrayEntryValues();
        setTempParcels([]);
        setTempServicingArrayEntries([]);
        setEditMode(false); //edit mode is always to be reset
      } else {
        console.log('impound does exist');
        const insComp: CompCodeResponse = impType.startsWith('IN')
          ? insuranceCompanies.find(
              (insuranceComp) => insuranceComp.companyCode === impound.insuranceComp.replace(/-/g, ''),
            )
          : impType.startsWith('MI')
            ? miCompanies.find((miComp) => miComp.companyCode === impound.insuranceComp.replace(/-/g, ''))
            : impType.startsWith('HA')
              ? hoaCompanies.find((hoaComp) => hoaComp.companyCode === impound.insuranceComp.replace(/-/g, ''))
              : null;
        const agentComp: CompCodeResponse = insuranceCompanies.find(
          (insuranceComp) => insuranceComp.companyCode === impound.agentCompanyCode.replace(/-/g, ''),
        );
        const miscPayee: MiscPayeeResponseElement = miscPayees.payees.find(
          (miscPayee) => miscPayee.payeeNumber === impound.payeeNumber,
        );
        setFormInput({
          impoundExists: true,
          activeStatus: impound.activeStatus,
          loan: loanNumber,
          impoundType: impound.impoundType,
          renewalDte: impound.renewalDte ? new Date(impound.renewalDte * 1000) : null,
          servPremium: impound.servPremium.toFixed(2),
          servSupPremium: impound.servSupPremium.toFixed(2),
          servAmtPaid: impound.servAmtPaid,
          servSupAmtPaid: impound.servSupAmtPaid,
          insuranceComp: insComp
            ? `${insComp.companyCode} - ${insComp.name} - ${insComp.address1} ${insComp.city}, ${insComp.state} ${insComp.zip}`
            : '',
          payeeNumber: miscPayee
            ? `${miscPayee.payeeNumber} - ${miscPayee.nameOne} - ${miscPayee.addrOne} ${miscPayee.city}, ${miscPayee.state} ${miscPayee.zip}`
            : '',
          policyNum: impound.policyNum,
          agentCompanyCode: agentComp
            ? `${agentComp.companyCode} - ${agentComp.name} - ${agentComp.address1} ${agentComp.city}, ${agentComp.state} ${agentComp.zip}`
            : '',
          coverageAmt: impound.coverageAmt.toFixed(2),
          cancelationDte: impound.cancelDte ? new Date(impound.cancelDte * 1000) : null,
          escrowed: impound.escrowed,
          parcels: impound.parcels,
          servicingArrayEntries: impound.servicingArray,
          comment: impound.comment,
        });
        const convertedParcels: ParcelInput[] = impound.parcels.map((parcel) => {
          return {
            amountDue: parcel.amountDue.toFixed(2),
            parcelNo: parcel.parcelNo,
            amountPaid: parcel.amountPaid,
            assessorCode: parcel.assessorCode,
            supAmountDue: parcel.supAmountDue.toFixed(2),
            parcelStatus: parcel.parcelStatus === constants.impPolicyStatusEscrowed ? true : false,
            parcelStatusString: parcel.parcelStatus,
            supAmountPaid: parcel.supAmountPaid,
            parcelArrComment: parcel.parcelArrComment,
            $index: parcel.$index,
          };
        });
        clearParcelValues();
        setTempParcels(convertedParcels);
        const convertedServicingArrayEntries: ServicingArrayEntryInput[] = impound.servicingArray.map((entry) => {
          return {
            payAgent: entry.payAgent,
            unitNo: entry.unitNo,
            agentCompany: entry.agentCompany,
            servArrComment: entry.servArrComment,
            policyNo: entry.policyNo,
            servArrAmountPaid: entry.servArrAmountPaid,
            servArrSupAmountPaid: entry.servArrSupAmountPaid,
            policyStatus: entry.policyStatus === constants.impPolicyStatusEscrowed ? true : false,
            policyStatusString: entry.policyStatus, //directly stores the string value
            companyToPay: entry.companyToPay,
            servArrPayeeNumber: entry.servArrPayeeNumber,
            servArrCancelDate: entry.servArrCancelDate ? new Date(entry.servArrCancelDate * 1000) : null,
            servArrSupAmountDue: entry.servArrSupAmountDue.toFixed(2),
            servArrAmountDue: entry.servArrAmountDue.toFixed(2),
            servArrCoverage: entry.servArrCoverage.toFixed(2),
            $index: entry.$index,
          };
        });
        clearServicingArrayEntryValues();
        setTempServicingArrayEntries(convertedServicingArrayEntries);
        setEditMode(false); //edit mode is always to be reset
      }
      //SQC-530 Jacques Bega
      //errors should always be cleared when changing selected impound entry
      setFormErrors({});
      setParcelErrors({});
      setServicingArrayEntryErrors({});
    },
    [
      formInput.impoundType,
      props.hazImpounds,
      loanNumber,
      props.miImpounds,
      props.taxImpounds,
      props.hoaImpounds,
      props.grImpounds,
      insuranceCompanies,
      miCompanies,
      hoaCompanies,
      miscPayees,
      clearParcelValues,
      clearServicingArrayEntryValues,
    ],
  );

  const handleParcelChange = useCallback(
    (event: { target: { name: string; value: string } }) => {
      const name: string = event.target.name;
      let value: string | boolean = event.target.value;
      if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
        value = event.target.checked;
      }
      if ((name === 'amountDue' || name === 'supAmountDue') && typeof value === 'string') {
        //parse out dollar sign
        const dollarIndex: number = value.indexOf('$');
        value = value.slice(dollarIndex + 1);
      }
      setParcelValues((prevParcelValues) => {
        const newParcelValues: ParcelInput = { ...prevParcelValues };
        newParcelValues[name] = value;
        return newParcelValues;
      });
      if ((name === 'amountDue' || name === 'supAmountDue') && typeof value === 'string') {
        if (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value) >= 0) {
          const newParcelErrors: Record<string, string> = { ...parcelErrors };
          delete newParcelErrors[name];
          setParcelErrors(newParcelErrors);
        } else {
          setParcelErrors({
            ...parcelErrors,
            [name]: 'Invalid amount',
          });
        }
      }
    },
    [parcelErrors],
  );

  const handleServicingEntryChange = useCallback(
    (event: { target: { name: string; value: string | boolean | Date } }) => {
      const name: string = event.target.name;
      let value: string | boolean | Date = event.target.value;
      let dateString: string = '';
      let dateValue: Date = null;
      if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
        value = event.target.checked;
      }
      if (
        (name === 'servArrAmountDue' || name === 'servArrSupAmountDue' || name === 'servArrCoverage') &&
        typeof value === 'string'
      ) {
        const dollarIndex: number = value.indexOf('$');
        value = value.slice(dollarIndex + 1);
      }
      if (name === 'unitNo' && value && typeof value === 'string') {
        value = value.replace(/[^a-zA-Z0-9]/g, '');
      }
      //SQC-514, SQC-543 Jacques Bega
      //adding underscore, hyphen, forward/backward slash, space, and parenthesis as valid characters for IN impound policy no field
      if (name === 'policyNo' && value && typeof value === 'string') {
        if (formInput.impoundType.startsWith('IN')) {
          value = value.replace(/[^a-zA-Z0-9_/\\() -]/g, '');
        } else {
          value = value.replace(/[^a-zA-Z0-9]/g, '');
        }
      }
      if (name === 'servArrCancelDate' && typeof value === 'string') {
        value ? (dateString = `${value}T00:00:00-08:00`) : (dateString = ''); //convert to UTC-8 to ensure no time zone date shift occurs
        dateString ? (dateValue = new Date(dateString)) : (dateValue = null);
      }
      setServicingArrayEntryValues((prevServicingEntryValues) => {
        const newServicingArrayEntryValues: ServicingArrayEntryInput = { ...prevServicingEntryValues };
        name === 'servArrCancelDate'
          ? (newServicingArrayEntryValues[name] = dateValue)
          : (newServicingArrayEntryValues[name] = value);
        return newServicingArrayEntryValues;
      });
      if ((name === 'companyToPay' || name === 'agentCompany') && typeof value === 'string') {
        let matchedCompanies: Array<CompCodeResponse> = [];
        //compCode is extracted using the space delimiter, if extraction fails use empty string
        const compCode = typeof value === 'string' ? value.split(' ')[0] : '';
        matchedCompanies = insuranceCompanies.filter((insuranceComp) => insuranceComp.companyCode === compCode);
        if (matchedCompanies.length > 0) {
          const newServicingArrayEntryErrors: Record<string, string> = { ...servicingArrayEntryErrors };
          delete newServicingArrayEntryErrors[name];
          setServicingArrayEntryErrors(newServicingArrayEntryErrors);
        } else if (name === 'agentCompany' && value === '') {
          const newServicingArrayEntryErrors = { ...servicingArrayEntryErrors };
          delete newServicingArrayEntryErrors[name];
          setServicingArrayEntryErrors(newServicingArrayEntryErrors);
        } else {
          setServicingArrayEntryErrors({
            ...servicingArrayEntryErrors,
            [name]: 'Invalid Company Code',
          });
        }
      }
      if (
        (name === 'servArrAmountDue' || name === 'servArrSupAmountDue' || name === 'servArrCoverage') &&
        typeof value === 'string'
      ) {
        if (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value) >= 0) {
          const newServicingArrayEntryErrors: Record<string, string> = { ...servicingArrayEntryErrors };
          delete newServicingArrayEntryErrors[name];
          setServicingArrayEntryErrors(newServicingArrayEntryErrors);
        } else {
          setServicingArrayEntryErrors({
            ...servicingArrayEntryErrors,
            [name]: 'Invalid amount',
          });
        }
      }
    },
    [servicingArrayEntryErrors, insuranceCompanies, formInput.impoundType],
  );

  const handleAddParcel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (tempParcels.length < 10) {
        const newParcel: ParcelInput = {
          amountDue: parcelValues.amountDue,
          parcelNo: parcelValues.parcelNo,
          amountPaid: parcelValues.amountPaid,
          assessorCode: parcelValues.assessorCode,
          supAmountDue: parcelValues.supAmountDue,
          parcelStatus: parcelValues.parcelStatus,
          parcelStatusString: parcelValues.parcelStatus
            ? constants.impPolicyStatusEscrowed
            : constants.impPolicyStatusNonEscrowed,
          supAmountPaid: parcelValues.supAmountPaid,
          parcelArrComment: parcelValues.parcelArrComment,
          $index: tempParcels.length,
        };
        if (!newParcel.amountDue) {
          alert('Payment Due is required when adding a tax payee.');
          return;
        }
        if (!newParcel.parcelNo) {
          alert('Parcel No. is required when adding a tax payee.');
          return;
        }
        if (!newParcel.assessorCode) {
          alert('Assessor Code is required when adding a tax payee.');
          return;
        }
        if (!isNaN(parseFloat(newParcel.amountDue)) && !isNaN(parseFloat(newParcel.supAmountDue))) {
          const amountDue: number = parseFloat(newParcel.amountDue);
          const supAmountDue: number = parseFloat(newParcel.supAmountDue);
          if (amountDue === 0) {
            alert('Payment Due must be greater than zero.');
            return;
          }
          if (amountDue < newParcel.amountPaid) {
            alert(`Base Payment due cannot be less than Base Payment paid which is ${newParcel.amountPaid}`);
            return;
          }
          if (supAmountDue < newParcel.supAmountPaid) {
            alert(`Sup. Payment due cannot be less than Sup. Payment paid which is ${newParcel.supAmountPaid}`);
            return;
          }
        } else {
          if (isNaN(parseFloat(newParcel.amountDue))) {
            alert('Invalid value for Payment Due.');
            return;
          }
          if (isNaN(parseFloat(newParcel.supAmountDue))) {
            alert('Invalid value for Supplemental Payment Due.');
            return;
          }
        }
        setTempParcels([...tempParcels, newParcel]);

        setParcelValues({
          //clear values
          amountDue: '0.00',
          parcelNo: '',
          amountPaid: 0,
          assessorCode: '',
          supAmountDue: '0.00',
          parcelStatus: true,
          parcelStatusString: constants.impPolicyStatusEscrowed,
          supAmountPaid: 0,
          parcelArrComment: '',
          $index: 0,
        });
      } else {
        alert('Only ten tax payees can be present on a single servicing impound record.');
      }
    },
    [
      parcelValues.amountDue,
      parcelValues.supAmountDue,
      parcelValues.amountPaid,
      parcelValues.supAmountPaid,
      parcelValues.assessorCode,
      parcelValues.parcelNo,
      parcelValues.parcelStatus,
      parcelValues.parcelArrComment,
      tempParcels,
    ],
  );

  const handleAddServicingArrayEntry = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (tempServicingArrayEntries.length < 10) {
        const newServicingArrayEntry: ServicingArrayEntryInput = {
          payAgent: servicingArrayEntryValues.payAgent,
          unitNo: servicingArrayEntryValues.unitNo,
          agentCompany: servicingArrayEntryValues.agentCompany,
          servArrComment: servicingArrayEntryValues.servArrComment,
          policyNo: servicingArrayEntryValues.policyNo,
          servArrAmountPaid: servicingArrayEntryValues.servArrAmountPaid,
          servArrSupAmountPaid: servicingArrayEntryValues.servArrSupAmountPaid,
          policyStatus: servicingArrayEntryValues.policyStatus,
          policyStatusString: servicingArrayEntryValues.policyStatus
            ? constants.impPolicyStatusEscrowed
            : constants.impPolicyStatusNonEscrowed,
          companyToPay: servicingArrayEntryValues.companyToPay,
          servArrPayeeNumber: servicingArrayEntryValues.servArrPayeeNumber,
          servArrCancelDate: servicingArrayEntryValues.servArrCancelDate,
          servArrSupAmountDue: servicingArrayEntryValues.servArrSupAmountDue,
          servArrAmountDue: servicingArrayEntryValues.servArrAmountDue,
          servArrCoverage: servicingArrayEntryValues.servArrCoverage,
          $index: tempServicingArrayEntries.length,
        };
        if (!newServicingArrayEntry.unitNo) {
          alert('Unit No. is required when adding a hazard insurance payee.');
          return;
        }
        if (!newServicingArrayEntry.companyToPay) {
          alert('Insurance Company is required when adding a hazard insurance payee.');
          return;
        }
        if (!newServicingArrayEntry.servArrAmountDue) {
          alert('Amount Due is required when adding a hazard insurance payee.');
          return;
        }
        if (!newServicingArrayEntry.policyNo) {
          alert('Policy No. is required when adding a hazard insurance payee.');
          return;
        }
        if (!newServicingArrayEntry.servArrCoverage) {
          alert('Coverage Amount is required when adding a hazard insurance payee.');
          return;
        }
        if (newServicingArrayEntry.payAgent && !newServicingArrayEntry.agentCompany) {
          alert('If Pay Agent is checked there must be an Agent Company specified');
          return;
        }
        if (
          !isNaN(parseFloat(newServicingArrayEntry.servArrAmountDue)) &&
          !isNaN(parseFloat(newServicingArrayEntry.servArrSupAmountDue))
        ) {
          const amountDue: number = parseFloat(newServicingArrayEntry.servArrAmountDue);
          const supAmountDue: number = parseFloat(newServicingArrayEntry.servArrSupAmountDue);
          if (amountDue === 0) {
            alert('Amount Due must be greater than zero.');
            return;
          }
          if (amountDue < newServicingArrayEntry.servArrAmountPaid) {
            alert(
              `Base Amount due cannot be less than Base Amount paid which is ${newServicingArrayEntry.servArrAmountPaid}`,
            );
            return;
          }
          if (supAmountDue < newServicingArrayEntry.servArrSupAmountPaid) {
            alert(
              `Sup. Amount due cannot be less than Sup. Amount paid which is ${newServicingArrayEntry.servArrSupAmountPaid}`,
            );
            return;
          }
        } else {
          if (isNaN(parseFloat(newServicingArrayEntry.servArrAmountDue))) {
            alert('Invalid value for Amount Due.');
            return;
          }
          if (isNaN(parseFloat(newServicingArrayEntry.servArrSupAmountDue))) {
            alert('Invalid value for Supplemental Amount Due.');
            return;
          }
        }
        setTempServicingArrayEntries([...tempServicingArrayEntries, newServicingArrayEntry]);
        setServicingArrayEntryValues({
          payAgent: false,
          unitNo: '1', //default unit to be 1 in the case of single payees
          agentCompany: '',
          servArrComment: '',
          policyNo: '',
          servArrAmountPaid: 0.0,
          servArrSupAmountPaid: 0.0,
          policyStatus: true,
          policyStatusString: constants.impPolicyStatusEscrowed, //default to escrowed
          companyToPay: '',
          servArrPayeeNumber: '',
          servArrCancelDate: null,
          servArrSupAmountDue: '0.00',
          servArrAmountDue: '0.00',
          servArrCoverage: '0.00',
          $index: 0,
        });
      } else {
        alert(
          `No more hazard impound entries of ${formInput.impoundType} type may be added for this renewal date. Backend storage is full.`,
        );
      }
    },
    [
      formInput.impoundType,
      servicingArrayEntryValues.unitNo,
      servicingArrayEntryValues.companyToPay,
      servicingArrayEntryValues.servArrAmountDue,
      servicingArrayEntryValues.servArrSupAmountDue,
      servicingArrayEntryValues.policyNo,
      servicingArrayEntryValues.policyStatus,
      servicingArrayEntryValues.servArrCoverage,
      servicingArrayEntryValues.servArrCancelDate,
      servicingArrayEntryValues.servArrComment,
      servicingArrayEntryValues.agentCompany,
      servicingArrayEntryValues.payAgent,
      servicingArrayEntryValues.servArrAmountPaid,
      servicingArrayEntryValues.servArrPayeeNumber,
      servicingArrayEntryValues.servArrSupAmountPaid,
      tempServicingArrayEntries,
    ],
  );

  const handleDeleteParcel = useCallback((index: number) => {
    setTempParcels((prevTempParcels) => {
      const updatedParcels = prevTempParcels
        .filter((parcel) => parcel.$index !== index) // Remove the deleted parcel
        .map((parcel, newIndex) => {
          return { ...parcel, $index: newIndex };
        }); //update the parcel indexes
      return updatedParcels;
    });
  }, []);

  const handleDeleteServicingArrayEntry = useCallback((index: number) => {
    setTempServicingArrayEntries((prevTempServicingEntries) => {
      const updatedEntries = prevTempServicingEntries
        .filter((entry) => entry.$index !== index) // Remove the deleted parcel
        .map((parcel, newIndex) => {
          return { ...parcel, $index: newIndex };
        }); //update the parcel indexes
      return updatedEntries;
    });
  }, []);

  const handleSelectParcel = useCallback(
    (parcel: ParcelInput) => {
      setEditMode(true);
      const assessor: TaxAssessorsResponse = taxAssessors?.find((a) => a.assessorCode === parcel.assessorCode);
      setParcelValues({
        ...parcel,
        assessorCode: assessor
          ? `${assessor.assessorCode} - ${assessor.nameOne} - ${assessor.addr1} ${assessor.city}, ${assessor.state} ${assessor.zip}`
          : parcel.assessorCode,
      });
    },
    [taxAssessors],
  );

  const handleSelectServicingArrayEntry = useCallback(
    (entry: ServicingArrayEntryInput) => {
      setEditMode(true);
      //SQC-530 Jacques Bega
      //declaring booleans to denote whether incoming insurance/agent company code is valid
      let insCompInvalid: boolean = false;
      let agentCompInvalid: boolean = false;
      console.log(insuranceCompanies);
      const insComp: CompCodeResponse = insuranceCompanies.find(
        //SQC-530 match insurance company code to first block of the raw string
        (insComp) => insComp.companyCode === entry.companyToPay.split(' ')[0],
      );
      console.log(insComp);
      let agentComp: CompCodeResponse;
      if (!isStrEmptyOrNull(entry.agentCompany)) {
        //SQC-530 match agent company code to first block of the raw string
        agentComp = insuranceCompanies.find((insComp) => insComp.companyCode === entry.agentCompany.split(' ')[0]);
      }
      console.log(agentComp);
      let insCompDetails: string = '';
      if (!isObjEmptyOrNull(insComp)) {
        insCompDetails = `${insComp.companyCode} - ${insComp.name} - ${insComp.address1} ${insComp.city}, ${insComp.state} ${insComp.zip}`;
      } else {
        //SQC-514 SQC-530 Jacques Bega
        //Reporting error message should insurance comp fail to be found due to misc payee backup being used
        insCompInvalid = true;
      }
      let agentCompDetails: string = '';
      if (!isObjEmptyOrNull(agentComp)) {
        agentCompDetails = `${agentComp.companyCode} - ${agentComp.name} - ${agentComp.address1} ${agentComp.city}, ${agentComp.state} ${agentComp.zip}`;
      } else if (!isStrEmptyOrNull(entry.agentCompany)) {
        //SQC-530 agent company is invalid only if the field is not blank when the search fails
        //this is because the field is optional so a blank value is valid
        agentCompInvalid = true;
      }
      if (!isStrEmptyOrNull(insCompDetails)) {
        entry.companyToPay = insCompDetails;
      }
      if (!isStrEmptyOrNull(agentCompDetails)) {
        entry.agentCompany = agentCompDetails;
      }
      setServicingArrayEntryValues(entry);
      //SQC-530 Jacques Bega
      //updating the servicing array entry specific errors to set/clear messaging regarding the insurance/agent company fields
      setServicingArrayEntryErrors((prevServicingArrayEntryErrors) => {
        const newServicingArrayEntryErrors: Record<string, string> = { ...prevServicingArrayEntryErrors };
        if (insCompInvalid) {
          newServicingArrayEntryErrors.companyToPay = 'Invalid Company Code';
        } else {
          delete newServicingArrayEntryErrors.companyToPay;
        }
        if (agentCompInvalid) {
          newServicingArrayEntryErrors.agentCompany = 'Invalid Company Code';
        } else {
          delete newServicingArrayEntryErrors.agentCompany;
        }
        return newServicingArrayEntryErrors;
      });
    },
    [insuranceCompanies],
  );

  const handleEditParcel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (!parcelValues.amountDue) {
        alert('Payment Due is required when editing a tax payee.');
        return;
      }
      if (!parcelValues.parcelNo) {
        alert('Parcel No. is required when editing a tax payee.');
        return;
      }
      if (!parcelValues.assessorCode) {
        alert('Assessor Code is required when editing a tax payee.');
        return;
      }
      if (!isNaN(parseFloat(parcelValues.amountDue)) && !isNaN(parseFloat(parcelValues.supAmountDue))) {
        const amountDue: number = parseFloat(parcelValues.amountDue);
        const supAmountDue: number = parseFloat(parcelValues.supAmountDue);
        if (amountDue === 0) {
          alert('Payment Due must be greater than zero.');
          return;
        }
        if (amountDue < parcelValues.amountPaid) {
          alert(`Base Payment due cannot be less than Base Payment paid which is ${parcelValues.amountPaid}`);
          return;
        }
        if (supAmountDue < parcelValues.supAmountPaid) {
          alert(`Sup. Payment due cannot be less than Sup. Payment paid which is ${parcelValues.supAmountPaid}`);
          return;
        }
      } else {
        if (isNaN(parseFloat(parcelValues.amountDue))) {
          alert('Invalid value for Payment Due.');
          return;
        }
        if (isNaN(parseFloat(parcelValues.supAmountDue))) {
          alert('Invalid value for Supplemental Payment Due.');
          return;
        }
      }
      setTempParcels((prevParcels) =>
        prevParcels.map((parcel) =>
          parcel.$index === parcelValues.$index
            ? {
                //update the parcel with matching $index value
                ...parcel,
                amountDue: parcelValues.amountDue,
                parcelNo: parcelValues.parcelNo,
                amountPaid: parcelValues.amountPaid,
                assessorCode: parcelValues.assessorCode,
                supAmountDue: parcelValues.supAmountDue,
                parcelStatus: parcelValues.parcelStatus,
                parcelStatusString: parcelValues.parcelStatus
                  ? constants.impPolicyStatusEscrowed
                  : constants.impPolicyStatusNonEscrowed,
                supAmountPaid: parcelValues.supAmountPaid,
                parcelArrComment: parcelValues.parcelArrComment,
              }
            : parcel,
        ),
      );

      setParcelValues({
        //clear values
        amountDue: '0.00',
        parcelNo: '',
        amountPaid: 0,
        assessorCode: '',
        supAmountDue: '0.00',
        parcelStatus: true,
        parcelStatusString: constants.impPolicyStatusEscrowed,
        supAmountPaid: 0,
        parcelArrComment: '',
        $index: 0,
      });

      setEditMode(false);
    },
    [
      parcelValues.$index,
      parcelValues.amountDue,
      parcelValues.supAmountDue,
      parcelValues.amountPaid,
      parcelValues.supAmountPaid,
      parcelValues.assessorCode,
      parcelValues.parcelNo,
      parcelValues.parcelStatus,
      parcelValues.parcelArrComment,
    ],
  );

  const handleEditServicingArrayEntry = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (!servicingArrayEntryValues.unitNo) {
        alert('Unit No. is required when editing a hazard insurance payee.');
        return;
      }
      if (!servicingArrayEntryValues.companyToPay) {
        alert('Insurance Company is required when editing a hazard insurance payee.');
        return;
      }
      if (!servicingArrayEntryValues.servArrAmountDue) {
        alert('Amount Due is required when editing a hazard insurance payee.');
        return;
      }
      if (!servicingArrayEntryValues.policyNo) {
        alert('Policy No. is required when editing a hazard insurance payee.');
        return;
      }
      if (!servicingArrayEntryValues.servArrCoverage) {
        alert('Coverage Amount is required when editing a hazard insurance payee.');
        return;
      }
      if (servicingArrayEntryValues.payAgent && !servicingArrayEntryValues.agentCompany) {
        alert('If Pay Agent is checked there must be an Agent Company specified');
        return;
      }
      if (
        !isNaN(parseFloat(servicingArrayEntryValues.servArrAmountDue)) &&
        !isNaN(parseFloat(servicingArrayEntryValues.servArrSupAmountDue))
      ) {
        const amountDue: number = parseFloat(servicingArrayEntryValues.servArrAmountDue);
        const supAmountDue: number = parseFloat(servicingArrayEntryValues.servArrSupAmountDue);
        if (amountDue === 0) {
          alert('Amount Due must be greater than zero.');
          return;
        }
        if (amountDue < servicingArrayEntryValues.servArrAmountPaid) {
          alert(
            `Base Amount due cannot be less than Base Amount paid which is ${servicingArrayEntryValues.servArrAmountPaid}`,
          );
          return;
        }
        if (supAmountDue < servicingArrayEntryValues.servArrSupAmountPaid) {
          alert(
            `Sup. Amount due cannot be less than Sup. Amount paid which is ${servicingArrayEntryValues.servArrSupAmountPaid}`,
          );
          return;
        }
      } else {
        if (isNaN(parseFloat(servicingArrayEntryValues.servArrAmountDue))) {
          alert('Invalid value for Amount Due.');
          return;
        }
        if (isNaN(parseFloat(servicingArrayEntryValues.servArrSupAmountDue))) {
          alert('Invalid value for Supplemental Amount Due.');
          return;
        }
      }
      setTempServicingArrayEntries((prevEntries) =>
        prevEntries.map((entry) =>
          entry.$index === servicingArrayEntryValues.$index
            ? {
                //update the entry with matching $index value
                ...entry,
                payAgent: servicingArrayEntryValues.payAgent,
                unitNo: servicingArrayEntryValues.unitNo,
                agentCompany: servicingArrayEntryValues.agentCompany,
                servArrComment: servicingArrayEntryValues.servArrComment,
                //SQC-543 Jacques Bega
                //policyNo may contain spaces as a valid character, need to trim leading/trailing spaces as they would not be filtered out during input
                policyNo: servicingArrayEntryValues.policyNo.trim(),
                servArrAmountPaid: servicingArrayEntryValues.servArrAmountPaid,
                servArrSupAmountPaid: servicingArrayEntryValues.servArrSupAmountPaid,
                policyStatus: servicingArrayEntryValues.policyStatus,
                policyStatusString: servicingArrayEntryValues.policyStatus
                  ? constants.impPolicyStatusEscrowed
                  : constants.impPolicyStatusNonEscrowed,
                companyToPay: servicingArrayEntryValues.companyToPay,
                servArrPayeeNumber: servicingArrayEntryValues.servArrPayeeNumber,
                servArrCancelDate: servicingArrayEntryValues.servArrCancelDate,
                servArrSupAmountDue: servicingArrayEntryValues.servArrSupAmountDue,
                servArrAmountDue: servicingArrayEntryValues.servArrAmountDue,
                servArrCoverage: servicingArrayEntryValues.servArrCoverage,
              }
            : entry,
        ),
      );
      setServicingArrayEntryValues({
        //clear values
        payAgent: false,
        unitNo: '1', //default unit to be 1 in the case of single payees
        agentCompany: '',
        servArrComment: '',
        policyNo: '',
        servArrAmountPaid: 0.0,
        servArrSupAmountPaid: 0.0,
        policyStatus: true,
        policyStatusString: constants.impPolicyStatusEscrowed, //default to escrowed
        companyToPay: '',
        servArrPayeeNumber: '',
        servArrCancelDate: null,
        servArrSupAmountDue: '0.00',
        servArrAmountDue: '0.00',
        servArrCoverage: '0.00',
        $index: 0,
      });
      setEditMode(false);
    },
    [
      servicingArrayEntryValues.$index,
      servicingArrayEntryValues.unitNo,
      servicingArrayEntryValues.companyToPay,
      servicingArrayEntryValues.servArrAmountDue,
      servicingArrayEntryValues.servArrSupAmountDue,
      servicingArrayEntryValues.policyNo,
      servicingArrayEntryValues.policyStatus,
      servicingArrayEntryValues.servArrCoverage,
      servicingArrayEntryValues.servArrCancelDate,
      servicingArrayEntryValues.servArrComment,
      servicingArrayEntryValues.agentCompany,
      servicingArrayEntryValues.payAgent,
      servicingArrayEntryValues.servArrAmountPaid,
      servicingArrayEntryValues.servArrPayeeNumber,
      servicingArrayEntryValues.servArrSupAmountPaid,
    ],
  );

  const updateFormInput = useCallback(
    (event: {
      target: { name: string; value: string | boolean | Date; type: string; checked: string | boolean | Date };
    }) => {
      const name: string = event.target.name;
      let value: string | boolean | Date = event.target.value;
      if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
        value = event.target.checked;
      }
      if (event.target.type === 'date') {
        console.log(value);
        value && typeof value === 'string' ? (value = `${value}T00:00:00-08:00`) : (value = null); //convert to UTC-8 to ensure no time zone date shift occurs
        console.log(value);
        value && typeof value === 'string' ? (value = new Date(value)) : (value = null);
        console.log(value);
      }
      if (name === 'policyNum' && value && typeof value === 'string') {
        value = value.replace(/[^a-zA-Z0-9]/g, '');
      }
      if (name === 'insuranceComp' || name === 'agentCompanyCode' || name === 'payeeNumber') console.log(value);
      if (
        (name === 'servPremium' || name === 'servSupPremium' || name === 'coverageAmt') &&
        typeof value === 'string'
      ) {
        //parse out dollar sign for currency fields
        const dollarIndex: number = value.indexOf('$');
        value = value.slice(dollarIndex + 1);
      }
      setFormInput((prevFormInput) => {
        const newFormInput: FormInput = { ...prevFormInput };
        newFormInput[name] = value;
        return newFormInput;
      });

      if (
        (name === 'servPremium' || name === 'servSupPremium' || name === 'coverageAmt') &&
        typeof value === 'string'
      ) {
        if (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value) >= 0) {
          const newFormErrors: Record<string, string> = { ...formErrors };
          delete newFormErrors[name];
          setFormErrors(newFormErrors);
        } else {
          setFormErrors({
            ...formErrors,
            [name]: 'Invalid amount',
          });
        }
      }
      if ((name === 'insuranceComp' || name === 'agentCompanyCode') && typeof value === 'string') {
        let matchedCompanies: Array<CompCodeResponse> = [];
        //compCode is extracted using the space delimiter, if extraction fails use empty string
        const compCode = typeof value === 'string' ? value.split(' ')[0] : '';
        if (formInput.impoundType.startsWith('IN')) {
          matchedCompanies = insuranceCompanies.filter((insuranceComp) => insuranceComp.companyCode === compCode);
        } else if (formInput.impoundType.startsWith('MI')) {
          matchedCompanies = miCompanies.filter((insuranceComp) => insuranceComp.companyCode === compCode);
        } else if (formInput.impoundType.startsWith('HA')) {
          matchedCompanies = hoaCompanies.filter((hoaComp) => hoaComp.companyCode === compCode);
        }
        if (matchedCompanies.length > 0) {
          const newFormErrors: Record<string, string> = { ...formErrors };
          delete newFormErrors[name];
          setFormErrors(newFormErrors);
        } else if (name === 'agentCompanyCode' && value === '') {
          const newFormErrors = { ...formErrors };
          delete newFormErrors[name];
          setFormErrors(newFormErrors);
        } else {
          setFormErrors({
            ...formErrors,
            [name]: 'Invalid Company Code',
          });
        }
      }
      if (name === 'payeeNumber' && typeof value === 'string') {
        let matchedPayees: Array<MiscPayeeResponseElement> = [];
        //compCode is extracted using the space delimiter, if extraction fails use empty string
        const payeeNumber = typeof value === 'string' ? value.split(' ')[0] : '';
        if (formInput.impoundType.startsWith('GR')) {
          matchedPayees = miscPayees.payees.filter((miscPayee) => miscPayee.payeeNumber === payeeNumber);
        }
        if (matchedPayees.length > 0) {
          const newFormErrors: Record<string, string> = { ...formErrors };
          delete newFormErrors[name];
          setFormErrors(newFormErrors);
        } else {
          setFormErrors({
            ...formErrors,
            [name]: 'Invalid Payee Number',
          });
        }
      }
    },
    [formErrors, formInput.impoundType, insuranceCompanies, miCompanies, hoaCompanies, miscPayees],
  );

  const handleAssessorOption = useCallback(
    (assessor: TaxAssessorsResponse) => {
      handleParcelChange({
        target: {
          name: 'assessorCode',
          value: `${assessor.assessorCode} - ${assessor.nameOne} - ${assessor.addr1} ${assessor.city}, ${assessor.state} ${assessor.zip}`,
        },
      });
    },
    [handleParcelChange],
  );

  const handleInsCompCodeOption = useCallback(
    async (insuranceComp: CompCodeResponse) => {
      const event: CustomDropdownEvent = {
        //must define custom event to sent to form input
        target: {
          name: '',
          value: '',
          type: '',
          checked: false,
        },
      };
      event.target.name = 'insuranceComp';
      event.target.value = `${insuranceComp.companyCode} - ${insuranceComp.name} - ${insuranceComp.address1} ${insuranceComp.city}, ${insuranceComp.state} ${insuranceComp.zip}`;
      event.target.type = 'text';
      updateFormInput(event);
    },
    [updateFormInput],
  );

  const handleCompanyToPayOption = useCallback(
    async (insuranceComp: CompCodeResponse) => {
      const event: CustomDropdownEvent = {
        //must define custom event to sent to form input
        target: {
          name: '',
          value: '',
          type: '',
          checked: false,
        },
      };
      event.target.name = 'companyToPay';
      event.target.value = `${insuranceComp.companyCode} - ${insuranceComp.name} - ${insuranceComp.address1} ${insuranceComp.city}, ${insuranceComp.state} ${insuranceComp.zip}`;
      event.target.type = 'text';
      handleServicingEntryChange(event);
    },
    [handleServicingEntryChange],
  );

  const handleAgentCompanyOption = useCallback(
    async (agentComp: CompCodeResponse) => {
      const event: CustomDropdownEvent = {
        //must define custom event to sent to form input
        target: {
          name: '',
          value: '',
          type: '',
          checked: false, //set to false as dropdowns cannot be checked
        },
      };
      event.target.name = 'agentCompany';
      event.target.value = `${agentComp.companyCode} - ${agentComp.name} - ${agentComp.address1} ${agentComp.city}, ${agentComp.state} ${agentComp.zip}`;
      event.target.type = 'text';
      handleServicingEntryChange(event);
    },
    [handleServicingEntryChange],
  );

  const handlePayeeNumberOption = useCallback(
    async (payee: MiscPayeeResponseElement) => {
      const event: CustomDropdownEvent = {
        //must define custom event to sent to form input
        target: {
          name: '',
          value: '',
          type: '',
          checked: false,
        },
      };
      event.target.name = 'payeeNumber';
      event.target.value = `${payee.payeeNumber} - ${payee.nameOne} - ${payee.addrOne} ${payee.city}, ${payee.state} ${payee.zip}`;
      event.target.type = 'text';
      updateFormInput(event);
    },
    [updateFormInput],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const impoundGroup: string = formInput.impoundType.slice(0, 2);
      const impoundType: string = formInput.impoundType;
      const imp1EntryExists: boolean = props.impTypes.includes(impoundType);

      let hazEntryPresent: boolean = false;
      for (const entry of tempServicingArrayEntries) {
        if (!isStrEmptyOrNull(entry.unitNo) && !isStrEmptyOrNull(entry.policyNo) && impoundGroup === 'IN') {
          hazEntryPresent = true;
          break; //check whether temp entries contain any valid IN group entries
        }
      }

      if (!imp1EntryExists && !formInput.activeStatus) {
        alert('Deactivating a nonexistant record! Nothing to submit.');
        return;
      }
      if (!formInput.activeStatus && impoundGroup === 'MI' && formInput.servAmtPaid > 0) {
        alert('Cannot deactivate a fully/partly paid record.');
        return;
      }
      if (
        !formInput.activeStatus &&
        (impoundGroup === 'HA' || impoundGroup === 'GR') &&
        (formInput.servAmtPaid > 0 || formInput.servSupAmtPaid > 0)
      ) {
        alert('Cannot deactivate a fully/partly paid record.');
        return;
      }
      if (impoundGroup === 'TX' && tempParcels.length === 0) {
        alert('At least one tax payee must be present on tax impound to submit.');
        return;
      }
      if (impoundGroup === 'IN' && (tempServicingArrayEntries.length === 0 || !hazEntryPresent)) {
        alert('At least one hazard insurance payee must be present on hazard insurance impound to submit.');
        return;
      }
      if (
        (impoundGroup === 'MI' || impoundGroup === 'HA' || impoundGroup === 'GR') &&
        parseFloat(formInput.servPremium) === 0
      ) {
        alert('Payment Due must be greater than zero.');
        return;
      }
      if (
        (impoundGroup === 'MI' || impoundGroup === 'HA' || impoundGroup === 'GR') &&
        parseFloat(formInput.servPremium) < formInput.servAmtPaid
      ) {
        alert(`Payment due cannot be less than payment paid which is ${formInput.servAmtPaid}`);
        return;
      }
      if (
        (impoundGroup === 'HA' || impoundGroup === 'GR') &&
        parseFloat(formInput.servSupPremium) < formInput.servSupAmtPaid
      ) {
        alert(
          `Supplemental Payment due cannot be less than supplemental payment paid which is ${formInput.servSupAmtPaid}`,
        );
        return;
      }

      let renewalDte: string = ''; // yyyy/mm/dd format
      const sendData: { [key: string]: any } = {}; //send data is dynamically constructed so must use any type here
      sendData.active = true;
      sendData.activeStatus = formInput.activeStatus;
      if (formInput.renewalDte) {
        const year = formInput.renewalDte.getFullYear();
        const month = formInput.renewalDte.getMonth() + 1; //add one since date object is zero indexed
        const day = formInput.renewalDte.getDate();
        renewalDte = `${year}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
      }
      if (sendData.activeStatus) {
        if (impoundGroup === 'TX') {
          sendData.parcels = [...tempParcels]; //ensure to pass by copy not by reference
          sendData.parcels.forEach((parcel: { assessorCode: string; parcelStatus: boolean | string; parcelStatusString: string }) => {
            parcel.assessorCode = parcel.assessorCode.split(' ')[0];
            parcel.parcelStatus = parcel.parcelStatusString; //convert parcelStatus to character from boolean
          });
          while (sendData.parcels.length < 10) {
            //append blank parcels to array; this ensures that we clear out any undesired data
            sendData.parcels.push({
              amountDue: 0.0,
              parcelNo: '',
              amountPaid: 0.0,
              assessorCode: '',
              supAmountDue: 0.0,
              parcelStatus: '',
              supAmountPaid: 0.0,
              parcelArrComment: '',
              $index: sendData.parcels.length,
            });
          }
        }
        if (impoundGroup === 'IN') {
          sendData.servicingArray = [...tempServicingArrayEntries]; //ensure to pass by copy not by reference
          sendData.servicingArray.forEach(
            (entry: {
              unitNo: string;
              policyNo: string;
              servArrPayeeNumber: string;
              companyToPay: string;
              agentCompany: string;
              policyStatus: boolean | string;
              policyStatusString: string;
              servArrCancelDate: any;
            }) => {
              if (!isStrEmptyOrNull(entry.unitNo) && !isStrEmptyOrNull(entry.policyNo)) {
                const insuranceCompanyCode: string = entry.companyToPay.split(' ')[0]; //insurance and agent code must be stripped down to just the code
                const agentCompanyCode: string = entry.agentCompany.split(' ')[0];
                entry.companyToPay = insuranceCompanyCode;
                entry.agentCompany = agentCompanyCode;
              }
              //SQC-543 Jacques Bega
              //policyNo may contain spaces as a valid character, need to trim leading/trailing spaces as they would not be filtered out during input
              entry.policyNo = entry.policyNo.trim();
              entry.policyStatus = entry.policyStatusString; //convert policyStatus to character from boolean
              if (entry.servArrCancelDate !== null) {
                entry.servArrCancelDate = dateToEpoch(entry.servArrCancelDate); //convert Date into epoch number
              }
            },
          );
          while (sendData.servicingArray.length < 10) {
            //append blank parcels to array; this ensures that we clear out any undesired data
            sendData.servicingArray.push({
              payAgent: false,
              unitNo: '',
              agentCompany: '',
              servArrComment: '',
              policyNo: '',
              servArrAmountPaid: 0.0,
              servArrSupAmountPaid: 0.0,
              policyStatus: '',
              companyToPay: '',
              servArrPayeeNumber: '',
              servArrCancelDate: null,
              servArrSupAmountDue: 0.0,
              servArrAmountDue: 0.0,
              servArrCoverage: 0.0,
              $index: sendData.servicingArray.length,
            });
          }
        }
        if (impoundGroup === 'MI' || impoundGroup === 'HA' || impoundGroup === 'GR') {
          if (formInput.policyNum) sendData.policyNum = formInput.policyNum;
          if (formInput.servPremium) sendData.servPremium = formInput.servPremium;
          sendData.comment = formInput.comment;
          sendData.escrowed = formInput.escrowed;
        }
        if (impoundGroup === 'MI' || impoundGroup === 'HA') {
          if (formInput.insuranceComp) {
            const tempInsuranceComp: string = formInput.insuranceComp.split(' ')[0];
            sendData.insuranceComp = hyphenateCompCode(tempInsuranceComp);
          }
        }
        if ((impoundGroup === 'HA' || impoundGroup === 'GR') && formInput.servSupPremium)
          sendData.servSupPremium = formInput.servSupPremium;
        if (impoundGroup === 'MI') {
          if (formInput.cancelationDte) {
            try {
              sendData.cancelDte = dateToEpoch(formInput.cancelationDte);
            } catch (error) {
              console.error(error.message);
            }
          } else {
            sendData.cancelDte = null;
          }
        }
        if (impoundGroup === 'GR') {
          if (formInput.payeeNumber) {
            const tempPayeeNumber: string = formInput.payeeNumber.split(' ')[0];
            sendData.payeeNumber = tempPayeeNumber;
          }
        }
      }
      submitMutation.mutate({ impoundGroup, impoundType, sendData, renewalDte, imp1EntryExists });
    },
    [
      formInput.activeStatus,
      formInput.cancelationDte,
      formInput.comment,
      formInput.impoundType,
      formInput.insuranceComp,
      formInput.policyNum,
      formInput.renewalDte,
      formInput.servPremium,
      formInput.servAmtPaid,
      formInput.servSupPremium,
      formInput.servSupAmtPaid,
      formInput.escrowed,
      formInput.payeeNumber,
      props.impTypes,
      submitMutation,
      tempParcels,
      tempServicingArrayEntries,
    ],
  );

  //filter out any non tax, hazard insurance entries
  const [taxInsImpQids, setTaxInsImpQids] = useState<Array<QIDListResponse>>([]);
  useEffect(() => {
    if (impTypeStatus === 'pending' || impTypeStatus === 'error' || !impTypeQids) {
      setTaxInsImpQids([]);
    } else {
      const tempTaxInsImpQids: Array<QIDListResponse> = [];
      console.log('MI IMPOUNDS');
      console.log(props.miImpounds);
      console.log('MI ESCROWED');
      console.log(props.miEscrowed);
      if (!isObjEmpty(props.miImpounds, 'miImpounds')) {
        const tempArray: Array<QIDListResponse> = impTypeQids.filter(
          (qid) =>
            qid.code.startsWith('TX') ||
            qid.code.startsWith('IN') ||
            qid.code === 'HA01' ||
            qid.code === 'GR01' ||
            (qid.code.startsWith('MI') && props.miImpounds.find((impound) => impound.impoundType === qid.code)),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempTaxInsImpQids.push(tempArray[i]);
        }
      } else {
        const tempArray: Array<QIDListResponse> = impTypeQids.filter(
          (qid) =>
            qid.code.startsWith('TX') ||
            qid.code.startsWith('IN') ||
            qid.code === 'HA01' ||
            qid.code === 'GR01' ||
            ((qid.code === 'MI01' || qid.code === 'MI02' || qid.code === 'MI03' || qid.code === 'MI09') &&
              props.miEscrowed[qid.code]),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempTaxInsImpQids.push(tempArray[i]);
        }
      }
      setTaxInsImpQids(tempTaxInsImpQids);
    }
  }, [impTypeQids, impTypeStatus, props.miImpounds, props.miEscrowed]);

  const [filteredInsCompanies, setFilteredInsCompanies] = useState<Array<CompCodeResponse>>([]);
  useEffect(() => {
    if (
      insCompStatus === 'pending' ||
      miCompStatus === 'pending' ||
      hoaCompStatus === 'pending' ||
      insCompStatus === 'error' ||
      miCompStatus === 'error' ||
      hoaCompStatus === 'error' ||
      !insuranceCompanies ||
      !miCompanies ||
      !hoaCompanies
    ) {
      setFilteredInsCompanies([]);
    } else {
      const tempFilteredInsCompanies: Array<CompCodeResponse> = [];
      if (servicingArrayEntryValues.companyToPay && formInput.impoundType.startsWith('IN')) {
        const tempArray: Array<CompCodeResponse> = insuranceCompanies.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase().includes(servicingArrayEntryValues.companyToPay.toLowerCase()) ||
            insuranceComp.name.toLowerCase().includes(servicingArrayEntryValues.companyToPay.toLowerCase()) ||
            insuranceComp.address1.toLowerCase().includes(servicingArrayEntryValues.companyToPay.toLowerCase()) ||
            insuranceComp.city.toLowerCase().includes(servicingArrayEntryValues.companyToPay.toLowerCase()) ||
            insuranceComp.state.toLowerCase().includes(servicingArrayEntryValues.companyToPay.toLowerCase()) ||
            insuranceComp.zip.toLowerCase().includes(servicingArrayEntryValues.companyToPay.toLowerCase()),
        );
        const selfExcludeTempArray: Array<CompCodeResponse> = tempArray.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase() !==
            servicingArrayEntryValues.companyToPay.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredInsCompanies.push(selfExcludeTempArray[i]);
        }
      }
      if (formInput.insuranceComp && formInput.impoundType === 'MI01') {
        const tempArray: Array<CompCodeResponse> = miCompanies.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            insuranceComp.name.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            insuranceComp.address1.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            insuranceComp.city.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            insuranceComp.state.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            insuranceComp.zip.toLowerCase().includes(formInput.insuranceComp.toLowerCase()),
        );
        const selfExcludeTempArray: Array<CompCodeResponse> = tempArray.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase() !== formInput.insuranceComp.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredInsCompanies.push(selfExcludeTempArray[i]);
        }
      }
      if (formInput.insuranceComp && (formInput.impoundType === 'MI02' || formInput.impoundType === 'MI03')) {
        const tempArray: Array<CompCodeResponse> = miCompanies.filter(
          (insuranceComp) => insuranceComp.companyCode === constants.hudCompCode,
        ); //HUD company code
        const selfExcludeTempArray: Array<CompCodeResponse> = tempArray.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase() !== formInput.insuranceComp.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredInsCompanies.push(selfExcludeTempArray[i]);
        }
      }
      if (formInput.insuranceComp && formInput.impoundType === 'MI09') {
        const tempArray: Array<CompCodeResponse> = miCompanies.filter(
          (insuranceComp) => insuranceComp.companyCode === constants.usdaCompCode,
        ); //USDA company code
        const selfExcludeTempArray: Array<CompCodeResponse> = tempArray.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase() !== formInput.insuranceComp.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredInsCompanies.push(selfExcludeTempArray[i]);
        }
      }
      if (formInput.insuranceComp && formInput.impoundType === 'HA01') {
        const tempArray: Array<CompCodeResponse> = hoaCompanies.filter(
          (hoaComp) =>
            hoaComp.companyCode.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            hoaComp.name.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            hoaComp.address1.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            hoaComp.city.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            hoaComp.state.toLowerCase().includes(formInput.insuranceComp.toLowerCase()) ||
            hoaComp.zip.toLowerCase().includes(formInput.insuranceComp.toLowerCase()),
        );
        const selfExcludeTempArray: Array<CompCodeResponse> = tempArray.filter(
          (hoaComp) => hoaComp.companyCode.toLowerCase() !== formInput.insuranceComp.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredInsCompanies.push(selfExcludeTempArray[i]);
        }
      }
      setFilteredInsCompanies(tempFilteredInsCompanies);
    }
  }, [
    formInput.impoundType,
    formInput.insuranceComp,
    servicingArrayEntryValues.companyToPay,
    insuranceCompanies,
    miCompanies,
    hoaCompanies,
    insCompStatus,
    miCompStatus,
    hoaCompStatus,
  ]);

  const [filteredAgentCompanies, setFilteredAgentCompanies] = useState<Array<CompCodeResponse>>([]);
  useEffect(() => {
    if (insCompStatus === 'pending' || insCompStatus === 'error' || !insuranceCompanies) {
      setFilteredAgentCompanies([]);
    } else {
      const tempFilteredAgentCompanies: Array<CompCodeResponse> = [];
      if (servicingArrayEntryValues.agentCompany) {
        const tempArray: Array<CompCodeResponse> = insuranceCompanies.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase().includes(servicingArrayEntryValues.agentCompany.toLowerCase()) ||
            insuranceComp.name.toLowerCase().includes(servicingArrayEntryValues.agentCompany.toLowerCase()) ||
            insuranceComp.address1.toLowerCase().includes(servicingArrayEntryValues.agentCompany.toLowerCase()) ||
            insuranceComp.city.toLowerCase().includes(servicingArrayEntryValues.agentCompany.toLowerCase()) ||
            insuranceComp.state.toLowerCase().includes(servicingArrayEntryValues.agentCompany.toLowerCase()) ||
            insuranceComp.zip.toLowerCase().includes(servicingArrayEntryValues.agentCompany.toLowerCase()),
        );
        const selfExcludeTempArray: Array<CompCodeResponse> = tempArray.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase() !==
            servicingArrayEntryValues.agentCompany.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredAgentCompanies.push(selfExcludeTempArray[i]);
        }
      }
      setFilteredAgentCompanies(tempFilteredAgentCompanies);
    }
  }, [servicingArrayEntryValues.agentCompany, insuranceCompanies, insCompStatus]);

  const [filteredTaxAssessors, setFilteredTaxAssessors] = useState<Array<TaxAssessorsResponse>>([]);
  useEffect(() => {
    if (taxAssessStatus === 'pending' || taxAssessStatus === 'error' || !taxAssessors) {
      setFilteredTaxAssessors([]);
    } else {
      if (!parcelValues.assessorCode) {
        setFilteredTaxAssessors([]);
        return;
      }
      const query = parcelValues.assessorCode.toLowerCase();
      const filtered = taxAssessors.filter(
        (a) =>
          a.assessorCode.toLowerCase().includes(query) ||
          a.nameOne.toLowerCase().includes(query) ||
          a.addr1.toLowerCase().includes(query) ||
          a.city.toLowerCase().includes(query) ||
          a.state.toLowerCase().includes(query) ||
          a.zip.toLowerCase().includes(query),
      );
      setFilteredTaxAssessors(
        filtered.filter((a) => a.assessorCode.toLowerCase() !== parcelValues.assessorCode.split(' ')[0].toLowerCase()),
      );
    }
  }, [parcelValues.assessorCode, taxAssessors, taxAssessStatus]);

  const [filteredMiscPayees, setFilteredMiscPayees] = useState<Array<MiscPayeeResponseElement>>([]); 
  useEffect(() => {
    if (miscPayeesStatus === 'pending' || miscPayeesStatus === 'error' || !miscPayees) {
      setFilteredMiscPayees([]);
    } else {
      const tempFilteredMiscPayees: Array<MiscPayeeResponseElement> = [];
      if (formInput.payeeNumber && formInput.impoundType === 'GR01') {
        //filter out payees with null information
        const nonNullArray: Array<MiscPayeeResponseElement> = miscPayees.payees.filter(
          (miscPayee) =>
            miscPayee.payeeNumber &&
            miscPayee.nameOne &&
            miscPayee.addrOne &&
            miscPayee.city &&
            miscPayee.state &&
            miscPayee.zip,
        );
        const tempArray: Array<MiscPayeeResponseElement> = nonNullArray.filter(
          (miscPayee) =>
            miscPayee.payeeNumber.toLowerCase().includes(formInput.payeeNumber.toLowerCase()) ||
            miscPayee.nameOne.toLowerCase().includes(formInput.payeeNumber.toLowerCase()) ||
            miscPayee.addrOne.toLowerCase().includes(formInput.payeeNumber.toLowerCase()) ||
            miscPayee.city.toLowerCase().includes(formInput.payeeNumber.toLowerCase()) ||
            miscPayee.state.toLowerCase().includes(formInput.payeeNumber.toLowerCase()) ||
            miscPayee.zip.toLowerCase().includes(formInput.payeeNumber.toLowerCase()),
        );
        const selfExcludeTempArray: Array<MiscPayeeResponseElement> = tempArray.filter(
          (miscPayee) => miscPayee.payeeNumber.toLowerCase() !== formInput.payeeNumber.split(' ')[0].toLowerCase(),
        );
        for (let i = 0; i < selfExcludeTempArray.length; i++) {
          tempFilteredMiscPayees.push(selfExcludeTempArray[i]);
        }
      }
      setFilteredMiscPayees(tempFilteredMiscPayees);
    }
  }, [formInput.impoundType, formInput.payeeNumber, miscPayees, miscPayeesStatus]);

  if (
    impTypeStatus === 'pending' ||
    insCompStatus === 'pending' ||
    miCompStatus === 'pending' ||
    hoaCompStatus === 'pending' ||
    miscPayeesStatus === 'pending'
  )
    return <div>Loading...</div>;
  if (
    impTypeStatus === 'error' ||
    insCompStatus === 'error' ||
    miCompStatus === 'error' ||
    hoaCompStatus === 'error' ||
    miscPayeesStatus === 'error' ||
    taxAssessStatus === 'error'
  )
    return (
      <>
        <h2>Encountered errors while setting up Impound Form</h2>
        <ul>
          {impTypeStatus === 'error' && <li>{impTypeError.message}</li>}
          {insCompStatus === 'error' && <li>{insCompError.message}</li>}
          {miCompStatus === 'error' && <li>{miCompError.message}</li>}
          {hoaCompStatus === 'error' && <li>{hoaCompError.message}</li>}
          {miscPayeesStatus === 'error' && <li>{miscPayeesError.message}</li>}
          {taxAssessStatus === 'error' && <li>{taxAssessError.message}</li>}
        </ul>
      </>
    );

  return (
    <>
      <form className={css.impoundForm} onSubmit={handleSubmit}>
        <div className={'h3 semibold ' + css.formCaption}>Add/Edit&nbsp;Impound&nbsp;Form</div>
        <label>
          <span>Select an Impound Type:</span>
          <select name='impoundType' value={formInput.impoundType} onChange={setCurrentFormInput} required>
            <option value=''>Select Impound Type</option>
            {taxInsImpQids &&
              taxInsImpQids.length &&
              taxInsImpQids.map((qid, index) => (
                <option key={index} value={qid.code}>{`${qid.code} - ${qid.description}`}</option>
              ))}
          </select>
        </label>
        {formInput.impoundType && (
          <label>
            <span>Due Date:</span>
            <input
              type='date'
              name='renewalDte'
              value={
                formInput.renewalDte && !isNaN(formInput.renewalDte.getTime())
                  ? formInput.renewalDte.toISOString().split('T')[0]
                  : ''
              }
              onChange={setCurrentFormInput}
              required
            ></input>
          </label>
        )}
        {(formInput.impoundType.startsWith('MI') ||
          formInput.impoundType.startsWith('HA') ||
          formInput.impoundType.startsWith('GR')) &&
          formInput.renewalDte && (
            <label>
              <span>Payment Due: </span>
              <NumericFormat
                value={formInput.servPremium}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter payment amount'
                required
                name='servPremium'
                onValueChange={(values) => {
                  const { value } = values;
                  updateFormInput({ target: { name: 'servPremium', value, type: 'text', checked: false } });
                }}
              />
            </label>
          )}
        {formErrors.servPremium && <p className={css.errorMsg}>{formErrors.servPremium}</p>}
        {(formInput.impoundType.startsWith('HA') || formInput.impoundType.startsWith('GR')) && formInput.renewalDte && (
          <>
            <label>
              <span>Supplemental Payment Due: </span>
              <NumericFormat
                value={formInput.servSupPremium}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter supplemental payment amount'
                name='servPremium'
                onValueChange={(values) => {
                  const { value } = values;
                  updateFormInput({ target: { name: 'servSupPremium', value, type: 'text', checked: false } });
                }}
              />
            </label>
          </>
        )}
        {formErrors.servSupPremium && <p className={css.errorMsg}>{formErrors.servSupPremium}</p>}
        {formInput.impoundType.startsWith('TX') && formInput.renewalDte && (
          <>
            <label>
              <span>Tax Payee:</span>
              <input
                type='text'
                name='assessorCode'
                value={parcelValues.assessorCode}
                onChange={handleParcelChange}
                placeholder='Enter Tax Assessor Code/Name'
                autoComplete='off'
              />
              {filteredTaxAssessors.length !== 0 && (
                <div className={css.dropdown}>
                  <ul>
                    {filteredTaxAssessors.map((assessor, index) => (
                      <li key={index}>
                        <button type='button' onClick={() => handleAssessorOption(assessor)}>
                          {`${assessor.assessorCode} - ${assessor.nameOne} - ${assessor.addr1} ${assessor.city}, ${assessor.state} ${assessor.zip}`}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </label>
            <label>
              <span>Parcel No:</span>
              <input
                type='text'
                name='parcelNo'
                value={parcelValues.parcelNo}
                maxLength={50}
                onChange={handleParcelChange}
                placeholder='Enter parcel no'
                autoComplete='off'
              ></input>
            </label>
            <label>
              <span>Escrowed?</span>
              <input
                type='checkbox'
                name='parcelStatus'
                checked={parcelValues.parcelStatus}
                onChange={handleParcelChange}
              ></input>
            </label>
            <label>
              <span>Payment Due:</span>
              <NumericFormat
                value={parcelValues.amountDue}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter payment amount'
                name='amountDue'
                onValueChange={(values) => {
                  const { value } = values;
                  handleParcelChange({ target: { name: 'amountDue', value } });
                }}
              />
            </label>
            {parcelErrors.amountDue && <p className={css.errorMsg}>{parcelErrors.amountDue}</p>}
            <label>
              <span>Supplemental Payment Due (optional):</span>
              <NumericFormat
                value={parcelValues.supAmountDue}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter supplemental payment amount'
                name='supAmountDue'
                onValueChange={(values) => {
                  const { value } = values;
                  handleParcelChange({ target: { name: 'supAmountDue', value } });
                }}
              />
            </label>
            {parcelErrors.supAmountDue && <p className={css.errorMsg}>{parcelErrors.supAmountDue}</p>}
            <label>
              <span>Comment (optional):</span>
              <input
                type='text'
                name='parcelArrComment'
                value={parcelValues.parcelArrComment}
                onChange={handleParcelChange}
                autoComplete='off'
              ></input>
            </label>
            <button
              className={css.impoundFormButton}
              type='button'
              onClick={editMode ? handleEditParcel : handleAddParcel}
              disabled={
                !parcelValues.assessorCode ||
                !parcelValues.parcelNo ||
                !parcelValues.amountDue ||
                !isObjEmpty(parcelErrors, 'parcelErrors')
              }
            >
              {editMode ? 'Edit Tax Payee' : 'Add Tax Payee'}
            </button>
            {tempParcels && tempParcels.length > 0 && (
              <label>
                <span>Current Tax Payee(s)</span>
              </label>
            )}
            <ul>
              {taxAssessors &&
                taxAssessors.length > 0 &&
                tempParcels
                  .filter((parcel) => !isStrEmptyOrNull(parcel.parcelNo) && !isStrEmptyOrNull(parcel.assessorCode)) //only display non empty parcels that are not paid off
                  .map((parcel, index) => {
                    const parcelPayee: TaxAssessorsResponse = taxAssessors?.find(
                      (assessor) => assessor.assessorCode === parcel.assessorCode.split(' ')[0],
                    );
                    const parcelTotalAmountDue: number = parseFloat(parcel.amountDue) + parseFloat(parcel.supAmountDue);
                    return (
                      <li key={index}>
                        {parcelPayee ? parcelPayee.assessorCode : ''} -{' '}
                        {parcelPayee
                          ? `${parcelPayee.nameOne} - ${parcelPayee.addr1} ${parcelPayee.city}, ${parcelPayee.state} ${parcelPayee.zip}`
                          : ''}
                        ;{' '}
                        {
                          <NumericFormat
                            displayType='text'
                            value={parcelTotalAmountDue}
                            decimalScale={2}
                            fixedDecimalScale
                            prefix='$'
                            thousandSeparator
                          />
                        }
                        ; {parcel.parcelNo}
                        <button
                          className={css.editEntryButton}
                          type='button'
                          onClick={() => handleSelectParcel(parcel)}
                        >
                          EDIT
                        </button>
                        <button
                          className={css.deleteEntryButton}
                          type='button'
                          onClick={() => handleDeleteParcel(parcel.$index)}
                          disabled={parcel.amountPaid > 0 || parcel.supAmountPaid > 0 || editMode === true}
                        >
                          {parcel.amountPaid > 0 || parcel.supAmountPaid > 0 ? 'PAID' : 'X'}
                        </button>
                      </li>
                    );
                  })}
            </ul>
          </>
        )}
        {formInput.impoundType.startsWith('IN') && formInput.renewalDte && (
          <>
            <label>
              <span>Unit No:</span>
              <input
                type='text'
                name='unitNo'
                value={servicingArrayEntryValues.unitNo}
                maxLength={10}
                onChange={handleServicingEntryChange}
                autoComplete='off'
              ></input>
            </label>
            <label>
              <span>Insurance Company:</span>
              <input
                type='text'
                name='companyToPay'
                value={servicingArrayEntryValues.companyToPay}
                onChange={handleServicingEntryChange}
                placeholder='Enter Insurance Company Code/Name'
                autoComplete='off'
              ></input>
              {filteredInsCompanies.length !== 0 && (
                <div className={css.dropdown}>
                  <ul>
                    {filteredInsCompanies &&
                      filteredInsCompanies.length > 0 &&
                      filteredInsCompanies.map((insuranceComp, index) => (
                        <li>
                          <button
                            type='button'
                            key={index}
                            onClick={() => handleCompanyToPayOption(insuranceComp)}
                          >{`${insuranceComp.companyCode} - ${insuranceComp.name} - ${insuranceComp.address1} ${insuranceComp.city}, ${insuranceComp.state} ${insuranceComp.zip}`}</button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </label>
            {servicingArrayEntryErrors.companyToPay && (
              <p className={css.errorMsg}>{servicingArrayEntryErrors.companyToPay}</p>
            )}
            <label>
              <span>Agent Company (optional):</span>
              <input
                type='text'
                name='agentCompany'
                value={servicingArrayEntryValues.agentCompany}
                onChange={handleServicingEntryChange}
                placeholder='Enter Agent Company Code/Name'
                autoComplete='off'
              ></input>
              {filteredAgentCompanies.length !== 0 && (
                <div className={css.dropdown}>
                  <ul>
                    {filteredAgentCompanies &&
                      filteredAgentCompanies.length > 0 &&
                      filteredAgentCompanies.map((insuranceComp, index) => (
                        <li>
                          <button
                            type='button'
                            key={index}
                            onClick={() => handleAgentCompanyOption(insuranceComp)}
                          >{`${insuranceComp.companyCode} - ${insuranceComp.name} - ${insuranceComp.address1} ${insuranceComp.city}, ${insuranceComp.state} ${insuranceComp.zip}`}</button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </label>
            {servicingArrayEntryErrors.agentCompany && (
              <p className={css.errorMsg}>{servicingArrayEntryErrors.agentCompany}</p>
            )}
            <label>
              <span>Pay Agent?</span>
              <input
                type='checkbox'
                name='payAgent'
                checked={servicingArrayEntryValues.payAgent}
                onChange={handleServicingEntryChange}
              ></input>
            </label>
            <label>
              <span>Amount Due:</span>
              <NumericFormat
                value={servicingArrayEntryValues.servArrAmountDue}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter amount'
                name='servArrAmountDue'
                onValueChange={(values) => {
                  const { value } = values;
                  handleServicingEntryChange({ target: { name: 'servArrAmountDue', value } });
                }}
              />
            </label>
            {servicingArrayEntryErrors.servArrAmountDue && (
              <p className={css.errorMsg}>{servicingArrayEntryErrors.servArrAmountDue}</p>
            )}
            <label>
              <span>Supplemental Amount Due (optional):</span>
              <NumericFormat
                value={servicingArrayEntryValues.servArrSupAmountDue}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter supplemental amount'
                name='servArrSupAmountDue'
                onValueChange={(values) => {
                  const { value } = values;
                  handleServicingEntryChange({ target: { name: 'servArrSupAmountDue', value } });
                }}
              />
            </label>
            {servicingArrayEntryErrors.servArrSupAmountDue && (
              <p className={css.errorMsg}>{servicingArrayEntryErrors.servArrSupAmountDue}</p>
            )}
            <label>
              <span>Policy No:</span>
              <input
                type='text'
                name='policyNo'
                value={servicingArrayEntryValues.policyNo}
                maxLength={30}
                onChange={handleServicingEntryChange}
                autoComplete='off'
              ></input>
            </label>
            {formInput.impoundType.startsWith('IN') &&
              formInput.renewalDte &&
              (!props.isLoanFhaUsda || formInput.impoundType !== 'IN01') && ( //only non IN01 impounds can be non-escrowed; IN01 can only be non-escrowed on non FHA/USDA
                <label>
                  <span>Escrowed?</span>
                  <input
                    type='checkbox'
                    name='policyStatus'
                    checked={servicingArrayEntryValues.policyStatus}
                    onChange={handleServicingEntryChange}
                  ></input>
                </label>
              )}
            <label>
              <span>Coverage Amount:</span>
              <NumericFormat
                value={servicingArrayEntryValues.servArrCoverage}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                prefix='$'
                placeholder='Enter coverage amount'
                name='servArrCoverage'
                onValueChange={(values) => {
                  const { value } = values;
                  handleServicingEntryChange({ target: { name: 'servArrCoverage', value } });
                }}
              />
            </label>
            {servicingArrayEntryErrors.servArrCoverage && (
              <p className={css.errorMsg}>{servicingArrayEntryErrors.servArrCoverage}</p>
            )}
            <label>
              <span>Cancelation Date:</span>
              <input
                type='date'
                name='servArrCancelDate'
                value={
                  servicingArrayEntryValues.servArrCancelDate &&
                  !isNaN(servicingArrayEntryValues.servArrCancelDate.getTime())
                    ? servicingArrayEntryValues.servArrCancelDate.toISOString().split('T')[0]
                    : ''
                }
                onChange={handleServicingEntryChange}
              ></input>
            </label>
            <label>
              <span>Comment (optional):</span>
              <input
                type='text'
                name='servArrComment'
                value={servicingArrayEntryValues.servArrComment}
                onChange={handleServicingEntryChange}
                autoComplete='off'
              ></input>
            </label>
            <button
              className={css.impoundFormButton}
              type='button'
              onClick={editMode ? handleEditServicingArrayEntry : handleAddServicingArrayEntry}
              disabled={
                !servicingArrayEntryValues.unitNo ||
                !servicingArrayEntryValues.companyToPay ||
                !servicingArrayEntryValues.servArrAmountDue ||
                !servicingArrayEntryValues.policyNo ||
                !servicingArrayEntryValues.servArrCoverage ||
                !isObjEmpty(servicingArrayEntryErrors, 'servicingArrayEntryErrors')
              }
            >
              {editMode ? 'Edit Hazard Payee' : 'Add Hazard Payee'}
            </button>
            {tempServicingArrayEntries && tempServicingArrayEntries.length > 0 && (
              <label>
                <span>Current Hazard Payee(s)</span>
              </label>
            )}
            <ul>
              {insuranceCompanies &&
                insuranceCompanies.length > 0 &&
                tempServicingArrayEntries
                  .filter((entry) => !isStrEmptyOrNull(entry.unitNo) && !isStrEmptyOrNull(entry.policyNo))
                  .map((entry, index) => {
                    const insuranceCompCode: string = entry.companyToPay.split(' ')[0];
                    const hazPayee: CompCodeResponse = insuranceCompanies.find(
                      (insComp) => insComp.companyCode === insuranceCompCode,
                    );
                    const entryTotalAmountDue: number =
                      parseFloat(entry.servArrAmountDue) + parseFloat(entry.servArrSupAmountDue);
                    return (
                      <li key={index}>
                        {entry.unitNo}; {hazPayee ? hazPayee.companyCode : ''} -{' '}
                        {hazPayee
                          ? `${hazPayee.name} - ${hazPayee.address1} ${hazPayee.city}, ${hazPayee.state} ${hazPayee.zip}`
                          : ''}
                        ;{' '}
                        {
                          <NumericFormat
                            displayType='text'
                            value={entryTotalAmountDue}
                            decimalScale={2}
                            fixedDecimalScale
                            prefix='$'
                            thousandSeparator
                          />
                        }
                        ; {entry.policyNo}
                        <button
                          className={css.editEntryButton}
                          type='button'
                          onClick={() => handleSelectServicingArrayEntry(entry)}
                        >
                          EDIT
                        </button>
                        <button
                          className={css.deleteEntryButton}
                          type='button'
                          onClick={() => handleDeleteServicingArrayEntry(entry.$index)}
                          disabled={entry.servArrAmountPaid > 0 || entry.servArrSupAmountPaid > 0 || editMode === true}
                        >
                          {entry.servArrAmountPaid > 0 || entry.servArrSupAmountPaid > 0 ? 'PAID' : 'X'}
                        </button>
                      </li>
                    );
                  })}
            </ul>
          </>
        )}
        {(formInput.impoundType.startsWith('MI') || formInput.impoundType.startsWith('HA')) && formInput.renewalDte && (
          <>
            <label>
              <span>Insurance Company:</span>
              <input
                type='text'
                name='insuranceComp'
                value={formInput.insuranceComp}
                onChange={updateFormInput}
                placeholder='Enter Insurance Company Code/Name'
                required={formInput.impoundType.startsWith('MI') || formInput.impoundType.startsWith('HA')}
                autoComplete='off'
              ></input>
              {filteredInsCompanies.length !== 0 && (
                <div className={css.dropdown}>
                  <ul>
                    {filteredInsCompanies &&
                      filteredInsCompanies.length > 0 &&
                      filteredInsCompanies.map((insuranceComp, index) => (
                        <li>
                          <button
                            type='button'
                            key={index}
                            onClick={() => handleInsCompCodeOption(insuranceComp)}
                          >{`${insuranceComp.companyCode} - ${insuranceComp.name} - ${insuranceComp.address1} ${insuranceComp.city}, ${insuranceComp.state} ${insuranceComp.zip}`}</button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </label>
            {formErrors.insuranceComp && <p className={css.errorMsg}>{formErrors.insuranceComp}</p>}
          </>
        )}
        {formInput.impoundType.startsWith('GR') && formInput.renewalDte && (
          <>
            <label>
              <span>Payee:</span>
              <input
                type='text'
                name='payeeNumber'
                value={formInput.payeeNumber}
                onChange={updateFormInput}
                placeholder='Enter Payee Number/Name'
                required={formInput.impoundType.startsWith('GR')}
                autoComplete='off'
              ></input>
              {filteredMiscPayees.length !== 0 && (
                <div className={css.dropdown}>
                  <ul>
                    {filteredMiscPayees &&
                      filteredMiscPayees.length > 0 &&
                      filteredMiscPayees.map((miscPayee, index) => (
                        <li>
                          <button
                            type='button'
                            key={index}
                            onClick={() => handlePayeeNumberOption(miscPayee)}
                          >{`${miscPayee.payeeNumber} - ${miscPayee.nameOne} - ${miscPayee.addrOne} ${miscPayee.city}, ${miscPayee.state} ${miscPayee.zip}`}</button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </label>
            {formErrors.payeeNumber && <p className={css.errorMsg}>{formErrors.payeeNumber}</p>}
          </>
        )}
        {(formInput.impoundType.startsWith('MI') ||
          formInput.impoundType.startsWith('HA') ||
          formInput.impoundType.startsWith('GR')) &&
          formInput.renewalDte && (
            <>
              <label>
                <span>Policy No:</span>
                <input
                  type='text'
                  name='policyNum'
                  value={formInput.policyNum}
                  maxLength={30}
                  onChange={updateFormInput}
                  required={
                    formInput.impoundType.startsWith('MI') ||
                    formInput.impoundType.startsWith('HA') ||
                    formInput.impoundType.startsWith('GR')
                  }
                  autoComplete='off'
                ></input>
              </label>
            </>
          )}
        {(formInput.impoundType.startsWith('MI') || formInput.impoundType.startsWith('GR') || formInput.impoundType.startsWith('HA')) && formInput.renewalDte && (
          <>
            <label>
              <span>Escrowed?</span>
              <input type='checkbox' name='escrowed' checked={formInput.escrowed} onChange={updateFormInput}></input>
            </label>
          </>
        )}
        {(formInput.impoundType === 'MI01' || formInput.impoundType === 'MI02') && formInput.renewalDte && (
          <label>
            <span>Cancelation Date:</span>
            <input
              type='date'
              name='cancelationDte'
              value={
                formInput.cancelationDte && !isNaN(formInput.cancelationDte.getTime())
                  ? formInput.cancelationDte.toISOString().split('T')[0]
                  : ''
              }
              onChange={updateFormInput}
            ></input>
          </label>
        )}
        {formInput.impoundType &&
          formInput.renewalDte &&
          (formInput.impoundType.startsWith('MI') ||
            formInput.impoundType.startsWith('HA') ||
            formInput.impoundType.startsWith('GR')) && (
            <label>
              <span>Comment (optional):</span>
              <input
                type='text'
                name='comment'
                value={formInput.comment}
                onChange={updateFormInput}
                autoComplete='off'
              ></input>
            </label>
          )}
        {formInput.impoundType && formInput.renewalDte && (
          <>
            <label>
              <span>Active?</span>
              <input
                className='check-box'
                type='checkbox'
                name='activeStatus'
                checked={formInput.activeStatus}
                onChange={updateFormInput}
              ></input>
            </label>
            <button className={css.impoundFormButton} type='submit' disabled={!isObjEmpty(formErrors, 'formErrors')}>
              Submit
            </button>
          </>
        )}
      </form>
      {StatusModal()}
    </>
  );
};

export default ImpoundForm;
